/**
 * /compliance — the Training Division compliance dashboard, inside Attest.
 *
 * Brian, 2026-09-13: "I want the Compliance dashboard to be visible through
 * the Attest demo." 2026-09-14: keep the v2 dashboard's look and layout —
 * red / yellow / green with a glyph for colour-blind readers — make the
 * different kinds of thing easy to toggle, and be able to tell people:
 * cannot-work is a phone call plus an email to the person, system admin,
 * medical director and supervisors; an educational concern goes to the
 * education committee. Contacts are collected by sub-role in Setup.
 *
 * Everything shown is computed by the server from the certified record plus
 * the rules layer (M29) — nothing here is typed in. Sending is M30.
 *
 * Layout (the v2 concept, ported): header strip · KPI tiles · tabs
 *   ⚑ Attention queue · Roster · Heat-map · Requirements · Setup
 * with a slide-over drawer for one person.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../lib/api.js';
import ErrorBanner from '../components/ErrorBanner.jsx';
import '../styles/compliance.css';
import { withLane } from '../lib/lane.js';
import LanePicker from '../components/LanePicker.jsx';

const DATE = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeZone: 'America/New_York' });
const DAY = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeZone: 'UTC' });
const when = (iso) =>
  !iso ? '—' : /^\d{4}-\d{2}-\d{2}$/.test(iso) ? DAY.format(new Date(`${iso}T00:00:00Z`)) : DATE.format(new Date(iso));
const h = (n) => `${Number(n ?? 0).toFixed(Number(n ?? 0) % 1 === 0 ? 0 : 1)}`;
const ago = (iso) => {
  const d = Math.round((Date.now() - Date.parse(iso)) / 86_400_000);
  return d <= 0 ? 'today' : d === 1 ? 'yesterday' : `${d}d ago`;
};

/* Every status carries a glyph as well as a colour. */
const GLYPH = { ok: '✓', warn: '●', serious: '▲', crit: '✖', info: 'ℹ', gray: '—' };
function Pill({ tone, children }) {
  return (
    <span className={`pill ${tone}`}>
      <span aria-hidden="true">{GLYPH[tone]}</span> {children}
    </span>
  );
}
const STANDING_TONE = { current: 'ok', due_soon: 'warn', expired: 'crit', never: 'crit', unknown: 'gray', none: 'gray' };
const STANDING_LABEL = { current: 'Current', due_soon: 'Due soon', expired: 'Expired', never: 'Never', unknown: 'Not set', none: 'None' };
const StandingPill = ({ value }) => <Pill tone={STANDING_TONE[value] ?? 'gray'}>{STANDING_LABEL[value] ?? value}</Pill>;

const WORST_TONE = { critical: 'crit', serious: 'serious', warning: 'warn', green: 'ok' };
const WORST_LABEL = { critical: "Can't work", serious: 'Out of compliance', warning: 'Watch', green: 'Green' };
const SEV_TONE = { critical: 'crit', serious: 'serious', warning: 'warn' };
const SEV_LABEL = { critical: 'CRITICAL', serious: 'Serious', warning: 'Warning' };
const TRAJ = {
  complete: ['ok', 'Complete'],
  on_pace: ['ok', 'On track'],
  behind: ['warn', 'Behind pace'],
  cant_make: ['serious', "Can't make it"],
  unknown: ['gray', 'No level'],
};
const KIND_LABEL = { credential: 'State credential', course: 'Required course', hours: 'CE hours', card: 'Card to verify' };
const CARD_TONE = { verified: 'ok', pending: 'warn', rejected: 'crit' };
const CARD_LABEL = { verified: 'Verified', pending: 'Awaiting verification', rejected: 'Rejected' };
const SUB_ROLE_LABEL = {
  system_admin: 'System admin',
  medical_director: 'Medical director',
  supervisor: 'Supervisor',
  education_committee: 'Education committee',
  training_officer: 'Training officer',
  person: 'The person',
};

function Chips({ lanes }) {
  return lanes.map((l) => (
    <span key={l} className={`chip ${l.replace(/[^A-Z0-9]/gi, '').toUpperCase()}`}>
      {l}
    </span>
  ));
}
function Bar({ pct }) {
  const c = pct >= 95 ? 'var(--cd-ok)' : pct >= 70 ? 'var(--cd-warn)' : 'var(--cd-serious)';
  return (
    <div className="bar" role="img" aria-label={`${Math.round(pct)} percent`}>
      <span style={{ width: `${Math.min(100, pct)}%`, background: c }} />
    </div>
  );
}
function worstCourse(p) {
  const s = p.credentials.map((c) => c.standing);
  if (s.includes('expired') || s.includes('never')) return 'expired';
  if (s.includes('due_soon')) return 'due_soon';
  return s.length ? 'current' : 'none';
}
function pacePct(p) {
  // Pace-adjusted cycle CE: earned against what the cycle expects by now.
  if (!p.hours.required) return null;
  const el = Math.max(0.02, p.trajectory.elapsed ?? 0);
  return Math.min(200, (100 * p.hours.total) / (p.hours.required.total * el));
}

/* ------------------------------------------------------------------------ */
export function CompliancePage() {
  const [data, setData] = useState(undefined);
  const [error, setError] = useState(null);
  const [agency, setAgency] = useState('');
  const [tab, setTab] = useState('queue');
  const [drawer, setDrawer] = useState(null); // seat key
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rosterFilter, setRosterFilter] = useState({ q: '', lane: '', level: '', stat: '' });

  const load = useCallback(async () => {
    try {
      const d = await apiFetch(withLane('/api/v1/compliance'));
      setData(d);
      setError(null);
    } catch (err) {
      setError(err);
      setData((cur) => (cur === undefined ? null : cur));
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 6000);
    return () => clearTimeout(t);
  }, [toast]);

  const say = (text, err = false) => setToast({ text, err });

  const act = async (fn, okText, workingText = 'Working…') => {
    setBusy(true);
    say(workingText);
    try {
      const out = await fn();
      await load();
      setError(null);
      if (okText) say(typeof okText === 'function' ? okText(out) : okText);
      return out ?? true;
    } catch (err) {
      say(err.message ?? 'That did not work.', true);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const people = useMemo(() => (data?.people ?? []).filter((p) => !agency || p.agency === agency), [data, agency]);
  const active = useMemo(() => people.filter((p) => p.status === 'active'), [people]);
  const attention = useMemo(() => (data?.attention ?? []).filter((a) => !agency || a.agency === agency), [data, agency]);
  const kpi = useMemo(
    () => ({
      active: active.length,
      excluded: people.length - active.length,
      green: active.filter((p) => p.worst === 'green').length,
      items: attention.length,
      crit: active.filter((p) => p.worst === 'critical').length,
      serious: active.filter((p) => p.worst === 'serious').length,
      cant: active.filter((p) => p.trajectory.status === 'cant_make').length,
      dueSoon: active.filter((p) => p.credentials.some((c) => c.standing === 'due_soon') || p.red_tier === 'due_soon').length,
      cards: new Set(people.flatMap((p) => (p.pending_cards ?? []).map((c) => c.id))).size,
    }),
    [people, active, attention]
  );

  const seatOf = (key) => (data?.people ?? []).find((p) => `${p.roster_id}|${p.agency}` === key);
  const openPerson = (p) => setDrawer(`${p.roster_id}|${p.agency}`);

  const notify = (p, kinds) =>
    act(
      () =>
        apiFetch(withLane('/api/v1/compliance/notify'), {
          method: 'POST',
          body: JSON.stringify({ roster_id: p.roster_id, agency_name: p.agency, ...(kinds ? { kinds } : {}) }),
        }),
      (o) => {
        const sent = o.recipients.filter((r) => r.sent);
        const missed = o.recipients.filter((r) => !r.sent);
        const reason = (r) => (r.reason === 'no_email' ? 'no email on file' : r.reason === 'mail_disabled' ? 'mail is off' : 'send failed');
        return (
          (sent.length ? `Emailed ${sent.map((r) => SUB_ROLE_LABEL[r.sub_role] ?? r.sub_role).join(', ')}.` : 'No email went out.') +
          (missed.length ? ` Not reached: ${missed.map((r) => `${SUB_ROLE_LABEL[r.sub_role] ?? r.sub_role} (${reason(r)})`).join(', ')}.` : '') +
          (o.call_required ? ` ☎ Phone call needed${o.phone ? `: ${o.phone}` : ' — no phone on file'}.` : '') +
          ' Logged.'
        );
      },
      'Sending…'
    );

  // One press for everyone who cannot work: the phone-call list, emailed and logged.
  const notifyAllCritical = async (list) => {
    setBusy(true);
    say(`Sending for ${list.length} people…`);
    let n = 0;
    for (const p of list) {
      try {
        await apiFetch(withLane('/api/v1/compliance/notify'), {
          method: 'POST',
          body: JSON.stringify({ roster_id: p.roster_id, agency_name: p.agency, kinds: ['credential'] }),
        });
        n += 1;
      } catch {
        /* reported in the count */
      }
    }
    await load();
    setBusy(false);
    say(`Logged ${n} of ${list.length}. ${data.mail_enabled ? 'Emails are on their way.' : 'Mail is off on this server — nothing was delivered.'} Each row shows who to call.`);
  };

  return (
    <div className="cd">
      <div className="cd__head">
        <div>
          <h1>Training Division — Compliance</h1>
          <div className="sub">
            {data?.settings
              ? `${data.settings.model_label} · cycle ${when(data.settings.cycle_start)} – ${when(data.settings.cycle_end)} · ${Math.round(
                  data.settings.elapsed * 100
                )}% elapsed · due soon = ${data.settings.due_soon_days} days`
              : 'Loading…'}
          </div>
        </div>
        {data && (!data.tenant || data.tenant.demo) && (
          <span className="cd__sample" title="Fake people, fake records — the tenant is AB Tech (DEMO)">
            DEMO DATA
          </span>
        )}
        {data && (
          <LanePicker
            tenant={data.tenant}
            tenants={data.tenants}
            onChange={() => {
              setAgency('');
              setDrawer(null);
              setData(undefined);
              load();
            }}
          />
        )}
        {data && (
          <div className="cd__viewas">
            View
            <select value={agency} onChange={(e) => setAgency(e.target.value)} aria-label="Agency">
              <option value="">All agencies ({data.agencies.length})</option>
              {data.agencies.map((g) => (
                <option key={g.agency} value={g.agency}>
                  {g.agency} ({g.people})
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      <ErrorBanner error={error} />

      {data === undefined && <p className="cd__empty">Loading…</p>}

      {data && (
        <div className="cd__wrap">
          <div className="cd__kpis" aria-live="polite">
            <div className="cd__kpi">
              <div className="n">{kpi.active}</div>
              <div className="l">
                Active personnel
                {kpi.excluded ? <span className="mini"> (+{kpi.excluded} on leave, excluded)</span> : null}
              </div>
            </div>
            <div className="cd__kpi ok">
              <div className="n">{kpi.green}</div>
              <div className="l">✓ Fully green — nothing needed</div>
            </div>
            <button type="button" className={`cd__kpi ${kpi.items ? 'hot serious' : 'ok'}`} onClick={() => setTab('queue')}>
              <div className="n">{kpi.items}</div>
              <div className="l">Open attention items</div>
            </button>
            <button type="button" className={`cd__kpi ${kpi.crit ? 'hot crit' : 'ok'}`} onClick={() => setTab('queue')}>
              <div className="n">{kpi.crit}</div>
              <div className="l">✖ Red-tier expired — can't work</div>
            </button>
            <div className={`cd__kpi ${kpi.serious ? 'serious' : 'ok'}`}>
              <div className="n">{kpi.serious}</div>
              <div className="l">▲ Required course expired or missing</div>
            </div>
            <div className={`cd__kpi ${kpi.cant ? 'serious' : 'ok'}`}>
              <div className="n">{kpi.cant}</div>
              <div className="l">▲ Can't make cycle at current rate</div>
            </div>
            <div className={`cd__kpi ${kpi.dueSoon ? 'warn' : 'ok'}`}>
              <div className="n">{kpi.dueSoon}</div>
              <div className="l">● Something due within {data.settings.due_soon_days} days</div>
            </div>
            <button type="button" className={`cd__kpi ${kpi.cards ? 'warn' : 'ok'}`} onClick={() => setTab('queue')}>
              <div className="n">{kpi.cards}</div>
              <div className="l">● Cards awaiting verification</div>
            </button>
          </div>

          <div className="cd__tabs" role="tablist">
            {[
              ['queue', '⚑ Attention queue'],
              ['roster', 'Roster'],
              ['heat', 'Heat-map'],
              ['seats', 'Seat gate'],
              ['rules', 'Requirements'],
              ['setup', 'Setup · contacts & log'],
            ].map(([k, label]) => (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={tab === k}
                className={`cd__tab ${tab === k ? 'on' : ''}`}
                onClick={() => setTab(k)}
              >
                {label}
              </button>
            ))}
          </div>

          {tab === 'queue' && (
            <Queue
              attention={attention}
              people={people}
              data={data}
              busy={busy}
              onOpen={openPerson}
              onNotify={notify}
              onNotifyAllCritical={() => notifyAllCritical(active.filter((p) => p.worst === 'critical'))}
            />
          )}
          {tab === 'roster' && (
            <Roster people={people} data={data} filter={rosterFilter} setFilter={setRosterFilter} showAgency={!agency} onOpen={openPerson} />
          )}
          {tab === 'heat' && (
            <Heat
              people={active}
              data={data}
              onPick={(f) => {
                setRosterFilter({ q: '', lane: f.lane ?? '', level: '', stat: '' });
                if (f.agency) setAgency(f.agency);
                setTab('roster');
                say(`Roster filtered to ${f.agency ?? f.lane}`);
              }}
            />
          )}
          {tab === 'seats' && <SeatGate data={data} agency={agency} setAgency={setAgency} onOpen={openPerson} />}
          {tab === 'rules' && <Rules data={data} agency={agency} busy={busy} act={act} />}
          {tab === 'setup' && <Setup data={data} agency={agency} busy={busy} act={act} />}
        </div>
      )}

      {drawer && seatOf(drawer) && (
        <Drawer
          person={seatOf(drawer)}
          data={data}
          busy={busy}
          onClose={() => setDrawer(null)}
          onNotify={notify}
          act={act}
          onSave={(body) =>
            act(
              () => apiFetch(`/api/v1/compliance/people/${seatOf(drawer).roster_id}`, { method: 'PUT', body: JSON.stringify(body) }),
              'Saved.'
            )
          }
        />
      )}

      {toast && (
        <div className={`cd__toast ${toast.err ? 'err' : ''}`} role="status">
          {toast.text}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------ */
function Queue({ attention, people, data, busy, onOpen, onNotify, onNotifyAllCritical }) {
  const [sev, setSev] = useState('');
  const [kind, setKind] = useState('');
  const [level, setLevel] = useState('');
  const [showAll, setShowAll] = useState(false);
  const seat = (a) => people.find((p) => p.roster_id === a.roster_id && p.agency === a.agency);
  const items = attention.filter((a) => (!sev || a.severity === sev) && (!kind || a.kind === kind) && (!level || a.level === level));
  const shown = showAll ? items : items.slice(0, 60);
  return (
    <div className="cd__panel" role="tabpanel">
      <div className="cd__note">
        Ranked worklist — compliant personnel don't appear here. <b>Notify</b> emails the person and whoever the routing
        names (cannot-work → system admin, medical director, supervisor; a course or hours gap → the education
        committee) and logs it. A critical item also shows who to call.
      </div>
      <div className="cd__toolbar">
        <select value={sev} onChange={(e) => setSev(e.target.value)} aria-label="Severity">
          <option value="">All severities</option>
          <option value="critical">✖ Critical</option>
          <option value="serious">▲ Serious</option>
          <option value="warning">● Warning</option>
        </select>
        <select value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Issue type">
          <option value="">All issue types</option>
          <option value="credential">State credential</option>
          <option value="course">Required course</option>
          <option value="hours">CE trajectory</option>
          <option value="card">Card to verify</option>
        </select>
        <select value={level} onChange={(e) => setLevel(e.target.value)} aria-label="Level">
          <option value="">All levels</option>
          {data.levels.map((l) => (
            <option key={l}>{l}</option>
          ))}
        </select>
        <span className="mini">{items.length} open items</span>
        {data.can_manage && attention.some((a) => a.severity === 'critical') && (
          <button type="button" className="cbtn sm danger" disabled={busy} onClick={onNotifyAllCritical} style={{ marginLeft: 'auto' }}>
            ☎ Notify everyone who cannot work ({attention.filter((a) => a.severity === 'critical').length})
          </button>
        )}
      </div>
      {items.length === 0 ? (
        <div className="cd__empty">Queue clear — everyone in scope is being handled. ☕</div>
      ) : (
        <>
          {shown.map((a, i) => {
            const p = seat(a);
            return (
              <div key={`${a.roster_id}|${a.agency}|${i}`} className={`qrow ${a.severity === 'critical' ? 'crit' : ''}`}>
                <div style={{ minWidth: 96 }}>
                  <Pill tone={SEV_TONE[a.severity]}>{SEV_LABEL[a.severity]}</Pill>
                </div>
                <div className="who">
                  <b onClick={() => p && onOpen(p)} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && p && onOpen(p)}>
                    {a.full_name}
                  </b>
                  <div className="mini">
                    {a.level ?? '—'} · {a.agency} {p && <Chips lanes={p.lanes} />}
                  </div>
                </div>
                <div className="issue">
                  {a.text}
                  <div className="mini">{KIND_LABEL[a.kind]}</div>
                </div>
                <div className="meta">
                  {p?.last_notified ? (
                    <>
                      <span className="state">notified {ago(p.last_notified.sent_at)}</span>
                      <div className="mini">{p.last_notified.recipients} recipients</div>
                    </>
                  ) : (
                    <span className="state">open</span>
                  )}
                </div>
                <div className="acts">
                  {a.severity === 'critical' && (
                    <a
                      className="cbtn sm danger"
                      href={p?.phone ? `tel:${p.phone}` : undefined}
                      title={p?.phone ? `Call ${p.phone}` : 'No phone on file'}
                    >
                      ☎ {p?.phone ?? 'no phone'}
                    </a>
                  )}
                  {data.can_manage && a.kind === 'card' && (
                    <button type="button" className="cbtn sm" onClick={() => p && onOpen(p)}>
                      Verify
                    </button>
                  )}
                  {data.can_manage && a.kind !== 'card' && (
                    <button type="button" className="cbtn sm" disabled={busy || !p} onClick={() => p && onNotify(p, [a.kind])}>
                      Notify
                    </button>
                  )}
                  <button type="button" className="cbtn sm ghost" onClick={() => p && onOpen(p)}>
                    Open
                  </button>
                </div>
              </div>
            );
          })}
          {items.length > 60 && (
            <div className="cd__empty">
              <button type="button" className="cbtn ghost sm" onClick={() => setShowAll((v) => !v)}>
                {showAll ? 'Show the first 60' : `…and ${items.length - 60} more — show all`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------ */
const TRAJ_ORD = { cant_make: 0, behind: 1, on_pace: 2, complete: 3, unknown: 4 };
const CRED_ORD = { expired: 0, due_soon: 1, current: 2, none: 3 };
const RED_ORD = { expired: 0, due_soon: 1, current: 2, unknown: 3 };
const WORST_ORD = { critical: 0, serious: 1, warning: 2, green: 3 };
function Roster({ people, data, filter, setFilter, showAgency, onOpen }) {
  const [sort, setSort] = useState({ k: 'worst', asc: true });
  const set = (patch) => setFilter({ ...filter, ...patch });
  const q = filter.q.trim().toLowerCase();
  let rows = people.filter(
    (p) =>
      (!q || p.full_name.toLowerCase().includes(q) || (p.external_id ?? '').includes(q)) &&
      (!filter.lane || p.lanes.includes(filter.lane)) &&
      (!filter.level || p.level === filter.level)
  );
  rows = rows.filter((p) => {
    const st = filter.stat;
    if (st === 'loa') return p.status !== 'active';
    if (p.status !== 'active') return !st;
    if (st === 'green') return p.worst === 'green';
    if (st === 'behind') return p.trajectory.status === 'behind';
    if (st === 'cant') return p.trajectory.status === 'cant_make';
    if (st === 'issue') return p.red_tier === 'expired' || p.red_tier === 'due_soon';
    if (st === 'course') return worstCourse(p) === 'expired';
    return true;
  });
  rows.sort((a, b) => {
    let v = 0;
    const k = sort.k;
    if (k === 'name') v = a.full_name.localeCompare(b.full_name);
    if (k === 'agency') v = a.agency.localeCompare(b.agency);
    if (k === 'level') v = (a.level ?? 'zz').localeCompare(b.level ?? 'zz');
    if (k === 'ce') v = (pacePct(a) ?? -1) - (pacePct(b) ?? -1);
    if (k === 'traj') v = TRAJ_ORD[a.trajectory.status] - TRAJ_ORD[b.trajectory.status];
    if (k === 'creds') v = CRED_ORD[worstCourse(a)] - CRED_ORD[worstCourse(b)];
    if (k === 'red') v = RED_ORD[a.red_tier] - RED_ORD[b.red_tier];
    if (k === 'worst') v = WORST_ORD[a.worst] - WORST_ORD[b.worst] || a.full_name.localeCompare(b.full_name);
    return sort.asc ? v : -v;
  });
  const th = (k, label, cls = '') => (
    <th
      className={`sortable ${cls}`}
      onClick={() => setSort({ k, asc: sort.k === k ? !sort.asc : true })}
      aria-sort={sort.k === k ? (sort.asc ? 'ascending' : 'descending') : 'none'}
    >
      {label}
      {sort.k === k ? (sort.asc ? ' ▲' : ' ▼') : ''}
    </th>
  );
  const csv = () => {
    const head = ['Name', 'ID', 'Agency', 'Level', 'Lanes', 'Status', 'State credential', 'Cycle CE', 'Required', 'Trajectory', 'Required courses', 'Standing'];
    const lines = rows.map((p) =>
      [
        p.full_name,
        p.external_id ?? '',
        p.agency,
        p.level ?? '',
        p.lanes.join(' '),
        p.status,
        p.state_credential_expires ?? '',
        h(p.hours.total),
        p.hours.required?.total ?? '',
        TRAJ[p.trajectory.status][1],
        p.credentials.map((c) => `${c.course_code}:${STANDING_LABEL[c.standing]}`).join(' '),
        WORST_LABEL[p.worst],
      ]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(',')
    );
    const blob = new Blob([[head.join(','), ...lines].join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `compliance-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };
  return (
    <div className="cd__panel" role="tabpanel">
      <div className="cd__toolbar">
        <input type="search" placeholder="Search name / ID…" value={filter.q} onChange={(e) => set({ q: e.target.value })} aria-label="Search" />
        <select value={filter.lane} onChange={(e) => set({ lane: e.target.value })} aria-label="Lane">
          <option value="">All lanes</option>
          {data.lanes.map((l) => (
            <option key={l}>{l}</option>
          ))}
        </select>
        <select value={filter.level} onChange={(e) => set({ level: e.target.value })} aria-label="Level">
          <option value="">All levels</option>
          {data.levels.map((l) => (
            <option key={l}>{l}</option>
          ))}
        </select>
        <select value={filter.stat} onChange={(e) => set({ stat: e.target.value })} aria-label="Status">
          <option value="">All statuses</option>
          <option value="green">✓ Fully green</option>
          <option value="behind">● Behind pace</option>
          <option value="cant">▲ Can't make it</option>
          <option value="course">✖ Required course expired / missing</option>
          <option value="issue">✖ State credential expired / expiring</option>
          <option value="loa">On leave / separated (excluded)</option>
        </select>
        <button type="button" className="cbtn ghost sm" onClick={csv}>
          Export CSV
        </button>
        <span className="mini">{rows.length} shown</span>
      </div>
      <div className="cd__scroll">
        <table>
          <thead>
            <tr>
              {th('name', 'Name')}
              {showAgency && th('agency', 'Agency')}
              {th('level', 'Level')}
              <th>Lanes</th>
              {th('ce', 'Cycle CE', 'num')}
              <th style={{ minWidth: 110 }}>Progress</th>
              {th('traj', 'Trajectory')}
              {th('creds', 'Required courses')}
              {th('red', 'State cred')}
              {th('worst', 'Standing')}
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => {
              const loa = p.status !== 'active';
              const pct = pacePct(p);
              return (
                <tr key={`${p.roster_id}|${p.agency}`} className="clickable" style={loa ? { opacity: 0.5 } : undefined} onClick={() => onOpen(p)}>
                  <td>
                    <b>{p.full_name}</b>
                    <div className="mini">
                      {p.external_id ? `#${p.external_id}` : 'unconfirmed'} · {p.level ?? 'no level'}
                      {p.level_inferred ? ' ?' : ''}
                      {loa && (
                        <>
                          {' '}
                          <span className="pill gray">{p.status}</span>
                        </>
                      )}
                    </div>
                  </td>
                  {showAgency && <td>{p.agency}</td>}
                  <td>{p.level ?? '—'}</td>
                  <td>
                    <Chips lanes={p.lanes} />
                  </td>
                  <td className="num">{p.hours.required ? `${h(p.hours.total)}/${p.hours.required.total} h` : `${h(p.hours.total)} h`}</td>
                  <td>{pct == null ? <span className="mini">—</span> : <Bar pct={pct} />}</td>
                  <td>{loa ? <Pill tone="gray">excluded</Pill> : <Pill tone={TRAJ[p.trajectory.status][0]}>{TRAJ[p.trajectory.status][1]}</Pill>}</td>
                  <td>
                    {p.credentials.length === 0 ? <span className="mini">none required</span> : <StandingPill value={worstCourse(p)} />}
                    {p.credentials.length > 0 && (
                      <div className="mini">
                        {p.credentials.map((c) => `${c.course_code}${c.lane ? `·${c.lane}` : ''} ${GLYPH[STANDING_TONE[c.standing]]}`).join('  ')}
                      </div>
                    )}
                  </td>
                  <td>
                    <StandingPill value={p.red_tier} />
                    {p.state_credential_expires && <div className="mini">{when(p.state_credential_expires)}</div>}
                  </td>
                  <td>{loa ? <Pill tone="gray">n/a</Pill> : <Pill tone={WORST_TONE[p.worst]}>{WORST_LABEL[p.worst]}</Pill>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {rows.length === 0 && <div className="cd__empty">Nobody matches those filters.</div>}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------ */
function hmColor(v) {
  if (v >= 110) return ['var(--sq700)', '#fff'];
  if (v >= 95) return ['var(--sq600)', '#fff'];
  if (v >= 85) return ['var(--sq500)', '#fff'];
  if (v >= 70) return ['var(--sq400)', '#04101f'];
  if (v >= 50) return ['var(--sq200)', '#04101f'];
  return ['var(--sq100)', '#04101f'];
}
function Heat({ people, data, onPick }) {
  const el = Math.max(0.02, data.settings.elapsed);
  const cell = (list, d) => {
    const vals = list.filter((p) => p.hours.required?.[d]).map((p) => Math.min(1.2, p.hours.earned[d] / p.hours.required[d]) / el);
    if (!vals.length) return null;
    return Math.round((100 * vals.reduce((a, b) => a + b, 0)) / vals.length);
  };
  const byAgency = new Map();
  const byLane = new Map();
  for (const p of people) {
    byAgency.set(p.agency, [...(byAgency.get(p.agency) ?? []), p]);
    for (const l of p.lanes) byLane.set(l, [...(byLane.get(l) ?? []), p]);
  }
  const block = (title, rows, pick) => (
    <>
      <tr>
        <th className="rowh" style={{ paddingTop: 14 }}>
          {title}
        </th>
        {data.domains.map((d) => (
          <th key={d.key}>{d.label}</th>
        ))}
      </tr>
      {[...rows.entries()]
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([name, list]) => (
          <tr key={name}>
            <th className="rowh">
              {name} <span className="mini">({list.length})</span>
            </th>
            {data.domains.map((d) => {
              const v = cell(list, d.key);
              if (v == null)
                return (
                  <td key={d.key} className="na">
                    —
                  </td>
                );
              const [bg, ink] = hmColor(v);
              return (
                <td
                  key={d.key}
                  className={v < 70 ? 'flag' : ''}
                  style={{ background: bg, color: ink }}
                  onClick={() => onPick(pick(name))}
                  title="Open the roster filtered to this row"
                >
                  {v}%{v < 70 ? ' ⚠' : ''}
                </td>
              );
            })}
          </tr>
        ))}
    </>
  );
  return (
    <div className="cd__panel" role="tabpanel">
      <div className="cd__note">
        Average pace-adjusted CE per category — 100% means the group has earned what the cycle expects by today. Dark = on
        or ahead of pace. Cells outlined in orange sit under 70% of pace: that is your next in-service topic. Click a cell to
        open the roster for that row.
      </div>
      <div style={{ overflow: 'auto' }}>
        <table className="hm">
          <tbody>
            {block('By agency', byAgency, (name) => ({ agency: name }))}
            {byLane.size > 0 && block('By lane', byLane, (name) => ({ lane: name }))}
          </tbody>
        </table>
      </div>
      <div className="legend">
        {[
          ['var(--sq100)', '<50%'],
          ['var(--sq200)', '50–69'],
          ['var(--sq400)', '70–84'],
          ['var(--sq500)', '85–94'],
          ['var(--sq600)', '95–109'],
          ['var(--sq700)', '110%+'],
        ].map(([bg, label]) => (
          <span key={label}>
            <span className="sw" style={{ background: bg }} />
            {label}
          </span>
        ))}
        <span style={{ marginLeft: 10 }}>
          <span className="sw" style={{ background: 'transparent', outline: '2px solid var(--cd-serious)' }} />
          under 70% of pace — schedule an in-service
        </span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------ */
function Drawer({ person: p, data, busy, onClose, onNotify, onSave, act }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    level: p.level ?? '',
    lanes: p.lanes.join(', '),
    status: p.status,
    state_credential_expires: p.state_credential_expires ?? '',
    phone: p.phone ?? '',
    notes: p.notes ?? '',
  });
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const t = p.trajectory;
  const pct = pacePct(p);
  const el = Math.max(0.02, t.elapsed ?? 0);
  return (
    <>
      <div className="cd__ov" onClick={onClose} />
      <div className="cd cd__drawer" role="dialog" aria-modal="true" aria-label={p.full_name}>
        <div className="dhead">
          <div>
            <h2>{p.full_name}</h2>
            <div className="mini">
              {p.external_id ? `#${p.external_id}` : 'ID unconfirmed'} · {p.level ?? 'no level'}
              {p.level_inferred ? ' (from enrolment)' : ''} · {p.agency} <Chips lanes={p.lanes} />
            </div>
            <div className="mini">
              {p.email ?? 'no email'} · {p.phone ? <a href={`tel:${p.phone}`}>{p.phone}</a> : 'no phone'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <Link className="cbtn ghost sm" to={`/compliance/transcript/${p.roster_id}?agency=${encodeURIComponent(p.agency)}`}>
              CE transcript
            </Link>
            <Link className="cbtn ghost sm" to={`/people/${p.roster_id}/affiliations`}>
              Affiliations
            </Link>
            <button type="button" className="cbtn ghost sm" onClick={onClose}>
              ✕ Close
            </button>
          </div>
        </div>

        {p.status !== 'active' && (
          <div className="banner gray">
            On <b>{p.status}</b> — excluded from counts and reminder runs.
          </div>
        )}
        {p.red_tier === 'expired' && p.status === 'active' && (
          <div className="banner crit">
            <b>✖ Cannot work.</b> State {p.level ?? 'EMS'} credential expired {when(p.state_credential_expires)}. A phone call is needed
            {p.phone ? ` — ${p.phone}` : ' (no phone on file)'}; Notify emails the person, system admin, medical director and supervisor.
          </div>
        )}
        {t.status === 'cant_make' && p.status === 'active' && (
          <div className="banner warn">
            <b>▲ Trajectory:</b> {h(p.hours.total)} of {p.hours.required.total} h with {Math.round(t.elapsed * 100)}% of the cycle gone — at
            this rate finishes near {h(t.projected)} h.
          </div>
        )}
        {t.status === 'behind' && p.status === 'active' && (
          <div className="banner warn">
            <b>● Behind pace</b> ({Math.round(t.pace * 100)}%) — recoverable.
          </div>
        )}
        {p.worst === 'green' && p.status === 'active' && (
          <div className="banner ok">
            <b>✓ Fully green.</b> Nothing needed.
          </div>
        )}

        {p.items.length > 0 && (
          <div className="sect">
            <h3>Needs attention</h3>
            {p.items.map((it, i) => (
              <div className="req" key={i}>
                <Pill tone={SEV_TONE[it.severity]}>{SEV_LABEL[it.severity]}</Pill>
                <div className="rl">{it.text}</div>
              </div>
            ))}
            {data.can_manage && (
              <div className="cd__actions" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <button type="button" className="cbtn sm" disabled={busy} onClick={() => onNotify(p)}>
                  Notify everyone this concerns
                </button>
                {p.last_notified && (
                  <span className="mini">
                    last notified {ago(p.last_notified.sent_at)} · {p.last_notified.recipients} recipients
                    {p.last_notified.call_required ? ' · call was required' : ''}
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        <CardsSection person={p} data={data} busy={busy} act={act} />

        <div className="sect">
          <h3>Required courses and skills — {p.agency}</h3>
          {p.credentials.length === 0 ? (
            <div className="mini">No requirement applies to a {p.level ?? 'person with no level'} at {p.agency}.</div>
          ) : (
            p.credentials.map((c) => (
              <div className="req" key={c.rule_id}>
                <div className="rl">
                  <b>{c.course_code}</b> {c.course_name}
                  <div className="mini">
                    every {c.interval_months} months{c.lane ? ` on the ${c.lane} lane` : c.scope === 'system' ? ' (floor)' : ` (${p.agency})`}
                    {c.last_completed_at
                      ? ` · last ${when(c.last_completed_at)}${c.last_source === 'card' ? ' (card)' : ''} · due ${when(c.due_at)}`
                      : ' · no completion on record'}
                  </div>
                </div>
                <StandingPill value={c.standing} />
              </div>
            ))
          )}
        </div>

        <div className="sect">
          <h3>
            CE this cycle — {p.level ?? 'no level'} · {h(p.hours.total)}
            {p.hours.required ? `/${p.hours.required.total}` : ''} h {pct != null ? `· ${Math.round(pct)}% of pace` : ''}
          </h3>
          {p.hours.required ? (
            <>
              {p.hours.per_category.map((c) => (
                <div className="req" key={c.domain}>
                  <div className="rl">{c.label}</div>
                  <div className="mini">
                    {h(c.earned)}/{c.required} h
                  </div>
                  <div style={{ width: 120 }}>
                    <Bar pct={c.required ? (100 * Math.min(c.earned, c.required * 1.2)) / c.required / el : 0} />
                  </div>
                </div>
              ))}
              <div className="req">
                <div className="rl">Individual</div>
                <div className="mini">
                  {h(p.hours.individual)}/{p.hours.required.individual} h
                </div>
                <div style={{ width: 120 }}>
                  <Bar pct={(100 * p.hours.individual) / p.hours.required.individual / el} />
                </div>
              </div>
              <div className="mini" style={{ marginTop: 6 }}>
                Bars are pace-adjusted. Hours land in the category with the most room among those the course may count toward,
                capped at the Registry maximum.
              </div>
            </>
          ) : (
            <div className="mini">Set a level to measure hours against the table.</div>
          )}
        </div>

        <div className="sect">
          <h3>
            What {p.agency} says about {p.full_name.split(' ')[0]}
          </h3>
          {!editing ? (
            <>
              {[
                ['Level', p.level ? `${p.level}${p.level_inferred ? ' (from enrolment, not confirmed)' : ''}` : '—'],
                ['Lanes', p.lanes.length ? <Chips lanes={p.lanes} /> : '—'],
                ['Status', p.status],
                ['State credential expires', p.state_credential_expires ? when(p.state_credential_expires) : '—'],
                ['Phone', p.phone ?? '—'],
                ...(p.notes ? [['Notes', p.notes]] : []),
              ].map(([k, v]) => (
                <div className="req" key={k}>
                  <div className="rl">{k}</div>
                  <div>{v}</div>
                </div>
              ))}
              {data.can_manage && (
                <button type="button" className="cbtn ghost sm" onClick={() => setEditing(true)}>
                  Edit
                </button>
              )}
            </>
          ) : (
            <form
              className="cd__form"
              onSubmit={async (e) => {
                e.preventDefault();
                const ok = await onSave({
                  agency_name: p.agency,
                  level: form.level || null,
                  lanes: form.lanes
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean),
                  status: form.status,
                  state_credential_expires: form.state_credential_expires || null,
                  phone: form.phone || null,
                  notes: form.notes || null,
                });
                if (ok) setEditing(false);
              }}
            >
              <label className="field">
                <span>Level</span>
                <select value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })}>
                  <option value="">Not set</option>
                  {data.levels.map((l) => (
                    <option key={l}>{l}</option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>Lanes (comma-separated)</span>
                <input type="text" value={form.lanes} onChange={(e) => setForm({ ...form, lanes: e.target.value })} placeholder="QRV, FTO" />
              </label>
              <label className="field">
                <span>Status</span>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  <option value="active">Active</option>
                  <option value="leave">On leave</option>
                  <option value="separated">Separated</option>
                </select>
              </label>
              <label className="field">
                <span>State credential expires</span>
                <input
                  type="date"
                  value={form.state_credential_expires}
                  onChange={(e) => setForm({ ...form, state_credential_expires: e.target.value })}
                />
              </label>
              <label className="field">
                <span>Phone</span>
                <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </label>
              <label className="field full">
                <span>Notes</span>
                <input type="text" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </label>
              <div className="cd__actions full">
                <button type="submit" className="cbtn sm" disabled={busy}>
                  {busy ? 'Saving…' : 'Save'}
                </button>
                <button type="button" className="cbtn ghost sm" onClick={() => setEditing(false)}>
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------------ */
async function uploadCardFile(path, file) {
  return apiFetch(path, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/octet-stream', 'x-file-type': file.type || 'application/octet-stream' },
    body: file,
  });
}

function CardsSection({ person: p, data, busy, act }) {
  const [adding, setAdding] = useState(false);
  const [courses, setCourses] = useState(null);
  const [form, setForm] = useState({ course_id: '', issued_at: '', expires_at: '', issuer: '', card_ref: '', ce_hours: '' });
  const [file, setFile] = useState(null);
  useEffect(() => {
    if (adding && courses === null)
      apiFetch('/api/v1/courses?active=1')
        .then(setCourses)
        .catch(() => setCourses([]));
  }, [adding, courses]);
  const open = async (id) => {
    try {
      const { url } = await apiFetch(`/api/v1/compliance/credentials/${id}/open`);
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      alert(err.message);
    }
  };
  const cards = p.cards ?? [];
  return (
    <div className="sect">
      <h3>Cards from elsewhere</h3>
      <div className="mini" style={{ marginBottom: 6 }}>
        Anything run through Attest is certified automatically. Anything uploaded counts only after someone here verifies it
        and assigns its hours.
      </div>
      {cards.length === 0 && <div className="mini">None on file.</div>}
      {cards.map((c) => (
        <div className="req" key={c.id}>
          <div className="rl">
            <b>{c.course_code}</b> {c.course_name}
            <div className="mini">
              issued {when(c.issued_at)}
              {c.expires_at ? ` · expires ${when(c.expires_at)}` : ''}
              {c.issuer ? ` · ${c.issuer}` : ''}
              {c.card_ref ? ` · #${c.card_ref}` : ''}
              {' · '}
              {c.submitted_via === 'participant' ? 'from their phone' : 'filed by staff'} {ago(c.submitted_at)}
              {c.has_file && (
                <>
                  {' · '}
                  <a href="#open" onClick={(e) => { e.preventDefault(); open(c.id); }}>
                    open the photo
                  </a>
                </>
              )}
            </div>
          </div>
          <Pill tone={CARD_TONE[c.status]}>{CARD_LABEL[c.status]}</Pill>
          {data.can_manage && c.status === 'pending' && (
            <>
              <button
                type="button"
                className="cbtn sm"
                disabled={busy}
                onClick={() => {
                  // Anything uploaded is the agency's to verify AND to credit:
                  // hours never come from the person (Brian, 2026-09-14).
                  const raw = window.prompt(
                    `Verify ${c.course_code}. Hours to credit this cycle from this card? (blank = none — the card still counts for the requirement)`,
                    c.ce_hours ?? ''
                  );
                  if (raw === null) return;
                  const hours = raw.trim() === '' ? null : Number(raw);
                  if (hours !== null && (!Number.isFinite(hours) || hours < 0 || hours > 999)) {
                    window.alert('Hours must be a number between 0 and 999, or blank.');
                    return;
                  }
                  act(
                    () =>
                      apiFetch(`/api/v1/compliance/credentials/${c.id}`, {
                        method: 'PATCH',
                        body: JSON.stringify({ status: 'verified', ce_hours: hours }),
                      }),
                    `${c.course_code} card verified — it now counts${hours ? ` and credits ${hours} h` : ''}.`
                  );
                }}
              >
                ✓ Verify
              </button>
              <button
                type="button"
                className="cbtn sm ghost"
                disabled={busy}
                onClick={() => {
                  const reason = window.prompt('Why is this card rejected? (the person sees this)');
                  if (reason === null) return;
                  act(
                    () =>
                      apiFetch(`/api/v1/compliance/credentials/${c.id}`, {
                        method: 'PATCH',
                        body: JSON.stringify({ status: 'rejected', rejection_reason: reason || null }),
                      }),
                    'Card rejected.'
                  );
                }}
              >
                ✖ Reject
              </button>
            </>
          )}
        </div>
      ))}
      {data.can_manage && !adding && (
        <div className="cd__actions">
          <button type="button" className="cbtn ghost sm" onClick={() => setAdding(true)}>
            + File a card
          </button>
        </div>
      )}
      {adding && (
        <form
          className="cd__form"
          onSubmit={async (e) => {
            e.preventDefault();
            const ok = await act(async () => {
              const card = await apiFetch('/api/v1/compliance/credentials', {
                method: 'POST',
                body: JSON.stringify({
                  roster_id: p.roster_id,
                  course_id: form.course_id,
                  issued_at: form.issued_at,
                  expires_at: form.expires_at || null,
                  issuer: form.issuer || null,
                  card_ref: form.card_ref || null,
                  ce_hours: form.ce_hours === '' ? null : Number(form.ce_hours),
                  verified: true,
                }),
              });
              if (file) await uploadCardFile(`/api/v1/compliance/credentials/${card.id}/file`, file);
              return card;
            }, 'Card filed and verified — it now counts.');
            if (ok) {
              setAdding(false);
              setForm({ course_id: '', issued_at: '', expires_at: '', issuer: '', card_ref: '', ce_hours: '' });
              setFile(null);
            }
          }}
        >
          <label className="field">
            <span>Course</span>
            <select value={form.course_id} onChange={(e) => setForm({ ...form, course_id: e.target.value })} required>
              <option value="">{courses ? 'Choose a course' : 'Loading…'}</option>
              {(courses ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code} — {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Issued</span>
            <input type="date" value={form.issued_at} onChange={(e) => setForm({ ...form, issued_at: e.target.value })} required />
          </label>
          <label className="field">
            <span>Expires (as printed; blank = the course term)</span>
            <input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} />
          </label>
          <label className="field">
            <span>Issuer</span>
            <input type="text" value={form.issuer} onChange={(e) => setForm({ ...form, issuer: e.target.value })} placeholder="AHA, NAEMT, Mission…" />
          </label>
          <label className="field">
            <span>Card / eCard number</span>
            <input type="text" value={form.card_ref} onChange={(e) => setForm({ ...form, card_ref: e.target.value })} />
          </label>
          <label className="field">
            <span>Hours to credit this cycle</span>
            <input type="number" min="0" max="999" step="0.5" value={form.ce_hours} onChange={(e) => setForm({ ...form, ce_hours: e.target.value })} />
          </label>
          <label className="field full">
            <span>Photo or PDF of the card</span>
            <input type="file" accept="image/*,application/pdf" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          </label>
          <div className="cd__actions full">
            <button type="submit" className="cbtn sm" disabled={busy || !form.course_id || !form.issued_at}>
              {busy ? 'Saving…' : 'File as verified'}
            </button>
            <button type="button" className="cbtn ghost sm" onClick={() => setAdding(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function SeatGate({ data, agency, setAgency, onOpen }) {
  const [seat, setSeat] = useState('FIELD');
  const [out, setOut] = useState(undefined);
  const [error, setError] = useState(null);
  const chosen = agency || data.agencies[0]?.agency || '';
  useEffect(() => {
    if (!chosen) return;
    setOut(undefined);
    apiFetch(withLane(`/api/v1/compliance/seats?agency=${encodeURIComponent(chosen)}&seat=${encodeURIComponent(seat)}`))
      .then((d) => {
        setOut(d);
        setError(null);
      })
      .catch((err) => {
        setError(err);
        setOut(null);
      });
  }, [chosen, seat, data]);
  const seatLabel = (sname) => (sname === 'FIELD' ? 'Field — base seat at their level' : `${sname} seat`);
  return (
    <div className="cd__panel" role="tabpanel">
      <div className="cd__note">
        The privileging gate: scheduling never interprets training data — the answer is yes or no per seat, with the
        blocking reason. Everyone at the agency is judged <em>as if</em> taking the seat, so it also shows who could be
        cleared for it. Due-soon is a warning on a yes, not a block.
      </div>
      <div className="cd__toolbar">
        {!agency && (
          <select value={chosen} onChange={(e) => setAgency(e.target.value)} aria-label="Agency">
            {data.agencies.map((g) => (
              <option key={g.agency} value={g.agency}>
                {g.agency}
              </option>
            ))}
          </select>
        )}
        <select value={seat} onChange={(e) => setSeat(e.target.value)} aria-label="Seat">
          {['FIELD', ...data.lanes].map((sname) => (
            <option key={sname} value={sname}>
              {seatLabel(sname)}
            </option>
          ))}
        </select>
        {out && (
          <span className="mini">
            {out.eligible} cleared · {out.blocked} not · as of {new Date(out.as_of).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' })}
          </span>
        )}
      </div>
      {error && <div className="mini">{error.message}</div>}
      {out === undefined && <div className="cd__empty">Working…</div>}
      {out && (
        <div className="seatgrid">
          {out.people.map((p) => (
            <div key={p.roster_id} className={`seatcard ${p.eligible ? '' : 'no'}`} onClick={() => onOpen(data.people.find((x) => x.roster_id === p.roster_id && x.agency === p.agency))} role="button" tabIndex={0}>
              <div className={`yn ${p.eligible ? 'y' : 'n'}`} aria-label={p.eligible ? 'yes' : 'no'}>
                {p.eligible ? '✓' : '✖'}
              </div>
              <div>
                <b>{p.full_name}</b> <span className="mini">{p.level ?? 'no level'}{seat !== 'FIELD' && !p.holds_lane ? ' · not tagged for this seat' : ''}</span>
                <div className="mini">
                  {p.eligible
                    ? p.warnings.length
                      ? `Cleared — ${p.warnings.join('; ')}`
                      : 'Cleared'
                    : p.blocking.join('; ')}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function scopeLabel(r) {
  if (r.scope === 'system') return 'System floor — the medical director';
  if (r.scope === 'lane') return `${r.agency_name} · ${r.lane} lane`;
  return r.agency_name;
}
function Rules({ data, agency, busy, act }) {
  const [adding, setAdding] = useState(false);
  const [courses, setCourses] = useState(null);
  const [form, setForm] = useState({
    scope: data.can_set_floor ? 'system' : 'agency',
    agency_name: agency || data.agencies[0]?.agency || '',
    lane: '',
    levels: [],
    what: 'course', // course | skill (M47)
    course_id: '',
    skill_code: '',
    interval_months: '24',
    notes: '',
  });
  useEffect(() => {
    if (adding && courses === null)
      apiFetch('/api/v1/courses?active=1')
        .then(setCourses)
        .catch(() => setCourses([]));
  }, [adding, courses]);
  const rules = (data.rules ?? []).filter((r) => !agency || r.scope === 'system' || r.agency_name === agency);
  const groups = new Map();
  for (const r of rules) groups.set(scopeLabel(r), [...(groups.get(scopeLabel(r)) ?? []), r]);
  return (
    <div className="cd__panel" role="tabpanel">
      <div className="cd__note">
        The floor applies to everyone under the System; an agency&apos;s rules apply to its people; a lane rule to the people
        it tags. An agency may add or tighten, never loosen — a looser rule is refused. A requirement is a course (from the{' '}
        <Link to="/catalog">catalog</Link>) or a skill sheet (from <Link to="/skill-sheets">Skill sheets</Link>) — a skill is met by a
        pass on that sheet.
      </div>
      {groups.size === 0 && <div className="cd__empty">No requirements set. Everyone is green by default.</div>}
      {[...groups.entries()].map(([label, list]) => (
        <div key={label} className="rulegroup">
          <h3>{label}</h3>
          {list.map((r) => (
            <div key={r.id} className="req" style={r.is_active ? undefined : { opacity: 0.55 }}>
              <div className="rl">
                {r.skill ? (
                  <>
                    <b>{r.skill.code}</b> {r.skill.title} <span className="mini">(skill sheet — a pass)</span>
                  </>
                ) : (
                  <>
                    <b>{r.course?.code}</b> {r.course?.name}
                  </>
                )}{' '}
                — every <b>{r.interval_months}</b> months for{' '}
                {r.levels?.length ? r.levels.join(', ') : 'every level'}
                {r.notes && <div className="mini">{r.notes}</div>}
              </div>
              {!r.is_active && <Pill tone="gray">paused</Pill>}
              {data.can_manage && (r.scope !== 'system' || data.can_set_floor) && (
                <button
                  type="button"
                  className="cbtn ghost sm"
                  disabled={busy}
                  onClick={() =>
                    act(
                      () =>
                        apiFetch(`/api/v1/compliance/rules/${r.id}`, {
                          method: 'PATCH',
                          body: JSON.stringify({ is_active: !r.is_active }),
                        }),
                      r.is_active ? 'Rule paused.' : 'Rule resumed.'
                    )
                  }
                >
                  {r.is_active ? 'Pause' : 'Resume'}
                </button>
              )}
            </div>
          ))}
        </div>
      ))}
      {data.can_manage && !adding && (
        <div className="cd__actions">
          <button type="button" className="cbtn sm" onClick={() => setAdding(true)}>
            + Add a requirement
          </button>
        </div>
      )}
      {adding && (
        <form
          className="cd__form"
          onSubmit={async (e) => {
            e.preventDefault();
            const ok = await act(
              () =>
                apiFetch('/api/v1/compliance/rules', {
                  method: 'POST',
                  body: JSON.stringify({
                    institution_id: data.institution_id,
                    scope: form.scope,
                    agency_name: form.scope === 'system' ? null : form.agency_name,
                    lane: form.scope === 'lane' ? form.lane : null,
                    levels: form.levels,
                    course_id: form.what === 'course' ? form.course_id : null,
                    skill_code: form.what === 'skill' ? form.skill_code : null,
                    interval_months: Number(form.interval_months),
                    notes: form.notes || null,
                  }),
                }),
              'Requirement added.'
            );
            if (ok) setAdding(false);
          }}
        >
          <label className="field">
            <span>Who sets it</span>
            <select value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })}>
              {data.can_set_floor && <option value="system">The medical director — everyone (floor)</option>}
              <option value="agency">An agency — its own people</option>
              <option value="lane">An agency — one lane (QRV, FTO…)</option>
            </select>
          </label>
          {form.scope !== 'system' && (
            <label className="field">
              <span>Agency</span>
              <input type="text" list="cd-agencies" value={form.agency_name} onChange={(e) => setForm({ ...form, agency_name: e.target.value })} required />
              <datalist id="cd-agencies">
                {data.agencies.map((g) => (
                  <option key={g.agency} value={g.agency} />
                ))}
              </datalist>
            </label>
          )}
          {form.scope === 'lane' && (
            <label className="field">
              <span>Lane</span>
              <input type="text" list="cd-lanes" value={form.lane} onChange={(e) => setForm({ ...form, lane: e.target.value })} required />
              <datalist id="cd-lanes">
                {data.lanes.map((l) => (
                  <option key={l} value={l} />
                ))}
              </datalist>
            </label>
          )}
          <label className="field">
            <span>Requires</span>
            <select value={form.what} onChange={(e) => setForm({ ...form, what: e.target.value })}>
              <option value="course">A course</option>
              <option value="skill">A skill sheet (a pass)</option>
            </select>
          </label>
          {form.what === 'course' ? (
            <label className="field">
              <span>Course</span>
              <select value={form.course_id} onChange={(e) => setForm({ ...form, course_id: e.target.value })} required>
                <option value="">{courses ? 'Choose a course' : 'Loading…'}</option>
                {(courses ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.code} — {c.name}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <label className="field">
              <span>Skill sheet</span>
              <select value={form.skill_code} onChange={(e) => setForm({ ...form, skill_code: e.target.value })} required>
                <option value="">{(data.skills ?? []).length ? 'Choose a skill sheet' : 'The library is empty — import under Skill sheets'}</option>
                {(data.skills ?? []).map((k) => (
                  <option key={k.code} value={k.code}>
                    {k.code} — {k.title}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="field">
            <span>Every how many months</span>
            <input
              type="number"
              min="1"
              max="120"
              value={form.interval_months}
              onChange={(e) => setForm({ ...form, interval_months: e.target.value })}
              required
            />
          </label>
          <fieldset className="levels full">
            <legend>Levels</legend>
            {data.levels.map((l) => (
              <label key={l}>
                <input
                  type="checkbox"
                  checked={form.levels.includes(l)}
                  onChange={(e) =>
                    setForm({ ...form, levels: e.target.checked ? [...form.levels, l] : form.levels.filter((x) => x !== l) })
                  }
                />{' '}
                {l}
              </label>
            ))}
            <span className="mini">none ticked = every level</span>
          </fieldset>
          <label className="field full">
            <span>Why (shown with the rule)</span>
            <input type="text" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </label>
          <div className="cd__actions full">
            <button type="submit" className="cbtn sm" disabled={busy || (form.what === 'course' ? !form.course_id : !form.skill_code)}>
              {busy ? 'Saving…' : 'Add'}
            </button>
            <button type="button" className="cbtn ghost sm" onClick={() => setAdding(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------ */
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const hourLabel = (h) => `${((h + 11) % 12) + 1}:00 ${h < 12 ? 'am' : 'pm'}`;

function NudgesPanel({ data, busy, act }) {
  const st = data.settings;
  const [preview, setPreview] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({
    nudge_days: (st.nudge_days ?? [30, 7, 0]).join(', '),
    nudge_min_gap_days: String(st.nudge_min_gap_days ?? 7),
    digest_weekday: String(st.digest_weekday ?? 1),
    digest_hour: String(st.digest_hour ?? 7),
  });
  const save = (patch) =>
    act(
      () =>
        apiFetch('/api/v1/compliance/settings', {
          method: 'PUT',
          body: JSON.stringify({
            institution_id: st.institution_id,
            model: st.model,
            cycle_start: st.cycle_start,
            cycle_end: st.cycle_end,
            due_soon_days: st.due_soon_days,
            nudges_enabled: st.nudges_enabled ?? false,
            nudge_days: st.nudge_days ?? [30, 7, 0],
            nudge_min_gap_days: st.nudge_min_gap_days ?? 7,
            digest_weekday: st.digest_weekday ?? 1,
            digest_hour: st.digest_hour ?? 7,
            ...patch,
          }),
        }),
      'Saved.'
    );
  const dryRun = async () => {
    setPreview('loading');
    try {
      setPreview(await apiFetch(withLane('/api/v1/compliance/nudges/run'), { method: 'POST', body: JSON.stringify({ dry_run: true }) }));
    } catch (err) {
      setPreview({ error: err.message });
    }
  };
  const held = preview && preview !== 'loading' && !preview.error ? preview.plan.nudges.filter((n) => n.skip).length : 0;
  return (
    <div className="rulegroup">
      <h3>On the clock</h3>
      <div className="req">
        <div className="rl">
          <b>{st.nudges_enabled ? '✓ Nudges and digests are on.' : '● Nudges and digests are off.'}</b>
          <div className="mini">
            The person hears at {(st.nudge_days ?? [30, 7, 0]).filter((d) => d > 0).join(' and ')} days before and the day something
            expires, never more than once every {st.nudge_min_gap_days ?? 7} days. Supervisors get their agency every{' '}
            {WEEKDAYS[st.digest_weekday ?? 1]} at {hourLabel(st.digest_hour ?? 7)}; the training officer the whole System; the
            medical director only when someone cannot work. Everything sent is logged below.
          </div>
        </div>
        {data.can_set_floor && (
          <>
            <button
              type="button"
              className={`cbtn sm ${st.nudges_enabled ? 'ghost' : ''}`}
              disabled={busy || st.defaulted}
              onClick={() => save({ nudges_enabled: !st.nudges_enabled })}
            >
              {st.nudges_enabled ? 'Switch off' : 'Switch on'}
            </button>
            <button type="button" className="cbtn sm ghost" onClick={() => setEdit((v) => !v)}>
              Schedule
            </button>
          </>
        )}
        <button type="button" className="cbtn sm ghost" disabled={busy || preview === 'loading'} onClick={dryRun}>
          {preview === 'loading' ? 'Working…' : 'Preview what would go out now'}
        </button>
      </div>
      {st.defaulted && <div className="mini">Save compliance settings (cycle and model) before switching nudges on.</div>}
      {edit && (
        <form
          className="cd__form"
          onSubmit={async (e) => {
            e.preventDefault();
            const ok = await save({
              nudge_days: form.nudge_days
                .split(',')
                .map((x) => Number(x.trim()))
                .filter((n) => Number.isInteger(n) && n >= 0),
              nudge_min_gap_days: Number(form.nudge_min_gap_days),
              digest_weekday: Number(form.digest_weekday),
              digest_hour: Number(form.digest_hour),
            });
            if (ok) setEdit(false);
          }}
        >
          <label className="field">
            <span>Nudge the person at (days before; 0 = the day it expires)</span>
            <input type="text" value={form.nudge_days} onChange={(e) => setForm({ ...form, nudge_days: e.target.value })} />
          </label>
          <label className="field">
            <span>Never more than once every (days)</span>
            <input
              type="number"
              min="1"
              max="90"
              value={form.nudge_min_gap_days}
              onChange={(e) => setForm({ ...form, nudge_min_gap_days: e.target.value })}
            />
          </label>
          <label className="field">
            <span>Digest day</span>
            <select value={form.digest_weekday} onChange={(e) => setForm({ ...form, digest_weekday: e.target.value })}>
              {WEEKDAYS.map((w, i) => (
                <option key={w} value={i}>
                  {w}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Digest hour (Asheville time)</span>
            <input type="number" min="0" max="23" value={form.digest_hour} onChange={(e) => setForm({ ...form, digest_hour: e.target.value })} />
          </label>
          <div className="cd__actions full">
            <button type="submit" className="cbtn sm" disabled={busy}>
              {busy ? 'Saving…' : 'Save schedule'}
            </button>
            <button type="button" className="cbtn ghost sm" onClick={() => setEdit(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}
      {preview && preview !== 'loading' && (
        <div className="sect" style={{ marginTop: 10 }}>
          <h3>Preview — nothing was sent</h3>
          {preview.error ? (
            <div className="mini">{preview.error}</div>
          ) : (
            <>
              <div className="mini">
                {preview.plan.nudges.length} person nudge{preview.plan.nudges.length === 1 ? '' : 's'} would go out
                {held ? ` (${held} held: no email on file, or nudged within the week)` : ''}; {preview.plan.digests.length} digest
                {preview.plan.digests.length === 1 ? '' : 's'}
                {preview.plan.digests.length ? '' : ` — digests go on ${WEEKDAYS[st.digest_weekday ?? 1]} at ${hourLabel(st.digest_hour ?? 7)}`}.
              </div>
              {preview.plan.nudges.slice(0, 40).map((n) => (
                <div className="req" key={`${n.roster_id}|${n.agency}`}>
                  <Pill tone={n.worst_stage === 0 ? 'serious' : 'warn'}>{n.worst_stage === 0 ? 'expired' : `${n.worst_stage}d`}</Pill>
                  <div className="rl">
                    <b>{n.full_name}</b> · {n.agency}
                    {n.skip ? <span className="mini"> · held: {n.skip === 'no_email' ? 'no email on file' : 'nudged within the week'}</span> : ''}
                    <div className="mini">{n.items.map((i) => i.text).join(' · ')}</div>
                  </div>
                </div>
              ))}
              {preview.plan.nudges.length > 40 && <div className="mini">…and {preview.plan.nudges.length - 40} more.</div>}
              {preview.plan.digests.map((d) => (
                <div className="req" key={d.key}>
                  <Pill tone="info">digest</Pill>
                  <div className="rl">
                    <b>{d.scope}</b> → {d.recipients.map((r) => r.full_name).join(', ')} ({SUB_ROLE_LABEL[d.sub_role]})
                    <div className="mini">
                      {d.summary.people} people · {d.summary.green} green · {d.summary.critical.length} cannot work ·{' '}
                      {d.summary.serious.length} out of compliance · {d.summary.due_soon.length} to watch
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Setup({ data, agency, busy, act }) {
  const [form, setForm] = useState({ sub_role: 'supervisor', agency_name: agency || '', full_name: '', email: '', phone: '' });
  const [log, setLog] = useState(null);
  useEffect(() => {
    apiFetch('/api/v1/compliance/notifications')
      .then(setLog)
      .catch(() => setLog([]));
  }, [data]);
  const contacts = (data.contacts ?? []).filter((c) => !agency || c.agency_name == null || c.agency_name === agency);
  const groups = new Map();
  for (const c of contacts) {
    const k = c.agency_name ?? 'Hub-wide (the System)';
    groups.set(k, [...(groups.get(k) ?? []), c]);
  }
  return (
    <div className="cd__panel" role="tabpanel">
      <div className="cd__note">
        <b>Who is told.</b> Cannot-work → the person, system admin, medical director, supervisor (and training officer if set) —
        and a phone call. A required-course or hours gap → the person and the education committee. An expiring credential → the
        person and supervisor. An agency&apos;s own contact for a sub-role is used where one exists; the hub&apos;s otherwise.{' '}
        {data.mail_enabled ? '' : <b>Mail is switched off on this server — sends will be logged as not delivered.</b>}
      </div>

      <NudgesPanel data={data} busy={busy} act={act} />

      <div className="rulegroup">
        <h3>Contacts by sub-role</h3>
      </div>
      {groups.size === 0 && <div className="cd__empty">No contacts yet. Add the medical director and a supervisor to start.</div>}
      {[...groups.entries()]
        .sort((a, b) => (a[0].startsWith('Hub') ? -1 : b[0].startsWith('Hub') ? 1 : a[0].localeCompare(b[0])))
        .map(([label, list]) => (
          <div key={label} className="rulegroup">
            <h3>{label}</h3>
            {list.map((c) => (
              <div key={c.id} className="req" style={c.is_active ? undefined : { opacity: 0.55 }}>
                <div className="rl">
                  <Pill tone="info">{SUB_ROLE_LABEL[c.sub_role]}</Pill> <b>{c.full_name}</b>
                  <div className="mini">
                    {c.email}
                    {c.phone ? ` · ${c.phone}` : ''}
                  </div>
                </div>
                {!c.is_active && <Pill tone="gray">inactive</Pill>}
                {data.can_manage && (
                  <button
                    type="button"
                    className="cbtn ghost sm"
                    disabled={busy}
                    onClick={() =>
                      act(
                        () =>
                          apiFetch(`/api/v1/compliance/contacts/${c.id}`, {
                            method: 'PATCH',
                            body: JSON.stringify({ is_active: !c.is_active }),
                          }),
                        c.is_active ? 'Contact set inactive.' : 'Contact reactivated.'
                      )
                    }
                  >
                    {c.is_active ? 'Deactivate' : 'Reactivate'}
                  </button>
                )}
              </div>
            ))}
          </div>
        ))}

      {data.can_manage && (
        <form
          className="cd__form"
          onSubmit={async (e) => {
            e.preventDefault();
            const ok = await act(
              () =>
                apiFetch('/api/v1/compliance/contacts', {
                  method: 'POST',
                  body: JSON.stringify({
                    institution_id: data.institution_id,
                    agency_name: form.agency_name || null,
                    sub_role: form.sub_role,
                    full_name: form.full_name,
                    email: form.email,
                    phone: form.phone || null,
                  }),
                }),
              'Contact added.'
            );
            if (ok) setForm({ ...form, full_name: '', email: '', phone: '' });
          }}
        >
          <label className="field">
            <span>Sub-role</span>
            <select value={form.sub_role} onChange={(e) => setForm({ ...form, sub_role: e.target.value })}>
              {data.sub_roles.map((r) => (
                <option key={r} value={r}>
                  {SUB_ROLE_LABEL[r]}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>For</span>
            <select value={form.agency_name} onChange={(e) => setForm({ ...form, agency_name: e.target.value })}>
              <option value="">Hub-wide (the System)</option>
              {data.agencies.map((g) => (
                <option key={g.agency} value={g.agency}>
                  {g.agency}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Name</span>
            <input type="text" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required />
          </label>
          <label className="field">
            <span>Email</span>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
          </label>
          <label className="field">
            <span>Phone</span>
            <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </label>
          <div className="cd__actions">
            <button type="submit" className="cbtn sm" disabled={busy}>
              {busy ? 'Saving…' : '+ Add contact'}
            </button>
          </div>
        </form>
      )}

      <div className="rulegroup">
        <h3>Notification log</h3>
        {log === null && <div className="mini">Loading…</div>}
        {log && log.length === 0 && <div className="mini">Nothing sent yet.</div>}
        {log &&
          log
            .filter((n) => !agency || n.agency_name === agency)
            .slice(0, 50)
            .map((n) => (
              <div key={n.id} className="req">
                <Pill tone={SEV_TONE[n.severity]}>{SEV_LABEL[n.severity]}</Pill>
                <div className="rl">
                  <b>{n.full_name ?? 'Digest'}</b> · {n.agency_name} · {when(n.sent_at)}
                  {n.trigger && n.trigger !== 'manual' ? ` · ${n.trigger === 'nudge' ? 'automatic nudge' : 'weekly digest'}` : ''}
                  {n.call_required ? ' · ☎ call required' : ''}
                  <div className="mini">{n.summary}</div>
                  <div className="mini">
                    {(n.recipients ?? []).map((r) => `${SUB_ROLE_LABEL[r.sub_role] ?? r.sub_role} ${r.sent ? '✓' : '✖'}`).join(' · ')}
                  </div>
                </div>
              </div>
            ))}
      </div>
    </div>
  );
}

export default CompliancePage;
